// Sales Targets — kullanıcı VEYA şube + dönem bazında satış hedefleri.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, FormField, ListPageHeader, EmptyState } from '../components/ui';
import { toast } from '../components/Toast';
import { fmtAmount } from '../utils/numberFormat';

export default function SalesTargetsPage() {
  const [list, setList] = useState([]);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null); // null=yeni, dolu=düzenleme
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);
  const emptyForm = {
    target_type: 'user', // 'user' | 'branch'
    user_id: '',
    branch_id: '',
    period_start: monthStart,
    period_end: monthEnd,
    target_amount: '',
    currency_code: 'TRY',
    dist_mode: 'share', // 'share' (paylaşım) | 'individual' (bireysel) | 'equal_rate' (aynı oran)
  };
  const [form, setForm] = useState(emptyForm);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tR, uR, bR] = await Promise.all([
        api.get('/crm/sales-targets'),
        api.get('/users'),
        api.get('/branches').catch(() => ({ data: { data: { items: [] } } })),
      ]);
      if (tR.data.success) setList(tR.data.data || []);
      if (uR.data.success) setUsers(uR.data.data || []);
      if (bR.data?.success) setBranches(bR.data.data?.items || bR.data.data || []);
    } catch (e) { toast.error('Yüklenemedi'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    if (form.target_type === 'user' && !form.user_id) { toast.error('Kullanıcı seçilmeli'); return; }
    if (form.target_type === 'branch' && !form.branch_id) { toast.error('Şube seçilmeli'); return; }
    if (!form.target_amount) { toast.error('Hedef tutar zorunlu'); return; }

    // Paylaşım modunda Dağıtım Payı toplamı kontrolü
    if (form.target_type === 'branch' && form.dist_mode === 'share') {
      try {
        const chk = await api.get('/branches/' + form.branch_id + '/personnel-share-summary');
        const d = chk.data?.data;
        if (d) {
          if (d.personnel_count === 0) {
            if (!window.confirm('Bu şubeye bağlı kullanıcısı olan personel bulunamadı. Yine de kaydedilsin mi?')) return;
          } else if (d.missing_share_count > 0) {
            const names = (d.missing_names || []).slice(0, 5).join(', ');
            if (!window.confirm(
              `⚠ ${d.missing_share_count} personelin "Şube Hedef Bonusu Dağıtım Payı (%)" boş:\n  ${names}${d.missing_share_count > 5 ? '\n  ...' : ''}\n\nBu personeller bonus alamayacak. Yine de kaydedilsin mi?`
            )) return;
          } else if (Math.abs(d.total_share - 100) > 0.01) {
            if (!window.confirm(
              `⚠ Şubedeki personellerin Dağıtım Payı toplamı %${d.total_share.toFixed(2)} (100 olması beklenir).\n\nBonus aynı oranda paylaşılır ama hedef toplama %100 değilse dengesizlik olabilir. Yine de kaydedilsin mi?`
            )) return;
          }
        }
      } catch (e) { /* sessiz — endpoint başarısız olursa kayda devam */ }
    }

    try {
      const body = {
        period_start: form.period_start,
        period_end: form.period_end,
        target_amount: form.target_amount,
        currency_code: form.currency_code,
        user_id:   form.target_type === 'user'   ? form.user_id   : null,
        branch_id: form.target_type === 'branch' ? form.branch_id : null,
        dist_mode: form.target_type === 'branch' ? form.dist_mode : 'share',
        use_dist_share: form.target_type === 'branch' ? (form.dist_mode !== 'individual') : true,
      };
      if (editId) {
        await api.put('/crm/sales-targets/' + editId, body);
        toast.success('Hedef güncellendi');
      } else {
        await api.post('/crm/sales-targets', body);
        toast.success('Hedef eklendi');
      }
      setShowForm(false);
      setEditId(null);
      setForm(emptyForm);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.message || 'Hata'); }
  };

  const openEdit = (t) => {
    setEditId(t.id);
    setForm({
      target_type: t.branch_id ? 'branch' : 'user',
      user_id: t.user_id || '',
      branch_id: t.branch_id || '',
      period_start: t.period_start ? new Date(t.period_start).toISOString().slice(0, 10) : monthStart,
      period_end:   t.period_end   ? new Date(t.period_end).toISOString().slice(0, 10)   : monthEnd,
      target_amount: t.target_amount,
      currency_code: t.currency_code || 'TRY',
      dist_mode: t.dist_mode || (t.use_dist_share === false ? 'individual' : 'share'),
    });
    setShowForm(true);
  };

  const del = async (id) => {
    if (!window.confirm('Bu hedef silinsin mi?')) return;
    try {
      await api.delete('/crm/sales-targets/' + id);
      toast.success('Silindi');
      fetchAll();
    } catch (e) { toast.error('Silinemedi'); }
  };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Satış Hedefleri"
        unit="hedef"
        count={list.length}
        onRefresh={fetchAll}
        refreshing={loading}
        actions={<Button size="sm" onClick={() => { setEditId(null); setForm(emptyForm); setShowForm(true); }}>+ Yeni Hedef</Button>}
      />

      <div style={{ padding: 16 }}>
        {list.length > 0 && (() => {
          // Para birimi bazında hedef + gerçekleşen + yüzde
          const byCurrency = {}; // { TRY: { target, achieved } }
          let userCnt = 0, branchCnt = 0;
          for (const t of list) {
            const cur = t.currency_code || 'TRY';
            if (!byCurrency[cur]) byCurrency[cur] = { target: 0, achieved: 0 };
            byCurrency[cur].target   += parseFloat(t.target_amount) || 0;
            byCurrency[cur].achieved += parseFloat(t.achieved_amount) || 0;
            if (t.branch_id) branchCnt++; else userCnt++;
          }
          return (
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10,
              marginBottom: 14,
            }}>
              <div style={S.summaryCard('#3b82f6')}>
                <div style={S.summaryLabel}>🎯 Toplam Hedef</div>
                <div style={S.summaryValue}>{list.length}</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                  👤 {userCnt} kullanıcı · 🏢 {branchCnt} şube
                </div>
              </div>
              {Object.entries(byCurrency).map(([cur, x]) => {
                const pct = x.target > 0 ? (x.achieved / x.target) * 100 : 0;
                const pctClamp = Math.min(100, Math.max(0, pct));
                const barColor = pct >= 100 ? '#10b981' : pct >= 75 ? '#3b82f6' : pct >= 50 ? '#f59e0b' : '#dc2626';
                return (
                  <div key={cur} style={S.summaryCard(barColor)}>
                    <div style={S.summaryLabel}>💰 Hedef / Gerçekleşen ({cur})</div>
                    <div style={{ fontSize: 14, fontFamily: 'JetBrains Mono, monospace', marginBottom: 4 }}>
                      <strong style={{ color: barColor }}>{fmtAmount(x.achieved)}</strong>
                      <span style={{ color: 'var(--text-tertiary)' }}> / {fmtAmount(x.target)} {cur}</span>
                    </div>
                    <div style={{ height: 6, background: 'var(--bg-tertiary)', borderRadius: 3, overflow: 'hidden', marginBottom: 4 }}>
                      <div style={{ height: '100%', width: pctClamp + '%', background: barColor, transition: 'width 0.4s ease' }} />
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: barColor }}>
                      %{pct.toFixed(1)} {pct >= 100 && '· ✓ aşıldı'}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {!loading && list.length === 0 ? (
          <EmptyState icon="🎯" title="Henüz satış hedefi tanımlanmamış" description="Plasiyer/satış elemanı veya şube için dönemsel hedef ekleyin. Pipeline tahmini ile birlikte performans takibi yapılır." />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
            {list.map(t => {
              const isBranch = !!t.branch_id;
              const label   = isBranch ? ('🏢 ' + (t.branch_name || '—')) : ('👤 ' + (t.user_name || '—'));
              const subCode = isBranch ? t.branch_code : null;
              const target   = parseFloat(t.target_amount) || 0;
              const achieved = parseFloat(t.achieved_amount) || 0;
              const pct      = target > 0 ? (achieved / target) * 100 : 0;
              const pctClamp = Math.min(100, Math.max(0, pct));
              const barColor = pct >= 100 ? '#10b981' : pct >= 75 ? '#3b82f6' : pct >= 50 ? '#f59e0b' : '#dc2626';
              return (
                <div key={t.id} style={S.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, alignItems: 'flex-start' }}>
                    <div>
                      <strong style={{ color: 'var(--text-primary)' }}>{label}</strong>
                      {subCode && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{subCode}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <span style={S.currencyBadge}>{t.currency_code}</span>
                      <button onClick={() => openEdit(t)} title="Düzenle" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--accent-primary)', fontSize: 14 }}>✎</button>
                      <button onClick={() => del(t.id)} title="Sil" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 16 }}>🗑</button>
                    </div>
                  </div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginBottom: 8 }}>
                    📅 {new Date(t.period_start).toLocaleDateString('tr-TR')} → {new Date(t.period_end).toLocaleDateString('tr-TR')}
                  </div>
                  {/* Hedef vs Gerçekleşen */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                    <div>
                      <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Hedef</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent-primary)', fontFamily: 'JetBrains Mono, monospace' }}>
                        {fmtAmount(target)} ₺
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Gerçekleşen</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: barColor, fontFamily: 'JetBrains Mono, monospace' }}>
                        {fmtAmount(achieved)} ₺
                      </div>
                    </div>
                  </div>
                  {/* İlerleme çubuğu + yüzde */}
                  <div style={{ height: 8, background: 'var(--bg-tertiary)', borderRadius: 4, overflow: 'hidden', marginBottom: 4 }}>
                    <div style={{ height: '100%', width: pctClamp + '%', background: barColor, transition: 'width 0.4s ease' }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                    <span style={{ fontWeight: 700, color: barColor }}>%{pct.toFixed(1)}</span>
                    <span style={{ color: 'var(--text-tertiary)' }}>
                      {pct >= 100 ? '✓ Hedef aşıldı' : ('Kalan: ' + fmtAmount(Math.max(0, target - achieved)) + ' ₺')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <Modal title={editId ? 'Hedef Düzenle' : 'Yeni Satış Hedefi'} size="md" onClose={() => { setShowForm(false); setEditId(null); }}
          footer={
            <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" onClick={() => { setShowForm(false); setEditId(null); }}>İptal</Button>
              <Button onClick={save}>💾 {editId ? 'Güncelle' : 'Kaydet'}</Button>
            </div>
          }>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {[
              { v: 'user',   l: '👤 Kullanıcı Hedefi' },
              { v: 'branch', l: '🏢 Şube Hedefi' },
            ].map(opt => (
              <button key={opt.v} onClick={() => setForm({ ...form, target_type: opt.v, user_id: '', branch_id: '' })}
                style={{
                  flex: 1, padding: '10px 12px', borderRadius: 8,
                  border: '2px solid ' + (form.target_type === opt.v ? 'var(--accent-primary)' : 'var(--border-secondary)'),
                  background: form.target_type === opt.v ? 'var(--accent-glow)' : 'var(--bg-secondary)',
                  cursor: 'pointer', fontWeight: 700, fontSize: 13,
                }}>{opt.l}</button>
            ))}
          </div>
          <div className="form-grid">
            {form.target_type === 'user' ? (
              <FormField label="Kullanıcı" required>
                <select value={form.user_id} onChange={e => setForm({ ...form, user_id: e.target.value })}>
                  <option value="">— Seçiniz —</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                </select>
              </FormField>
            ) : (
              <>
                <FormField label="Şube" required>
                  <select value={form.branch_id} onChange={e => setForm({ ...form, branch_id: e.target.value })}>
                    <option value="">— Seçiniz —</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.code ? b.code + ' - ' : ''}{b.name}</option>)}
                  </select>
                </FormField>
                <FormField label="Bonus Dağıtım Modu" hint={
                  form.dist_mode === 'share'
                    ? 'Şube toplam cirosu üzerinden hesaplanan bonus, personellerin Dağıtım Payı (%)\'larına göre paylaştırılır.'
                    : 'Her personel kendi cirosunu şube hedefiyle ayrı kıyaslar → kendi tier\'ı → kendi bonusu. Dağıtım Payı kullanılmaz.'
                }>
                  <select value={form.dist_mode} onChange={e => setForm({ ...form, dist_mode: e.target.value })}>
                    <option value="share">📊 Paylaşım (Dağıtım Payı'na göre)</option>
                    <option value="individual">👤 Bireysel (şube cirosu × ortak tier)</option>
                  </select>
                </FormField>
              </>
            )}
            <FormField label="Para Birimi">
              <select value={form.currency_code} onChange={e => setForm({ ...form, currency_code: e.target.value })}>
                <option value="TRY">TRY</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </FormField>
            <FormField label="Başlangıç" required>
              <input type="date" value={form.period_start} onChange={e => setForm({ ...form, period_start: e.target.value })} />
            </FormField>
            <FormField label="Bitiş" required>
              <input type="date" value={form.period_end} onChange={e => setForm({ ...form, period_end: e.target.value })} />
            </FormField>
            <FormField label="Hedef Tutar" required fullWidth>
              <input type="number" step="0.01" value={form.target_amount} onChange={e => setForm({ ...form, target_amount: e.target.value })} placeholder="500000" />
            </FormField>
          </div>
        </Modal>
      )}
    </div>
  );
}

const S = {
  card: {
    padding: 14,
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 'var(--r-md)',
    boxShadow: 'var(--shadow-xs)',
  },
  summaryCard: (color) => ({
    padding: '12px 14px',
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 10,
    borderLeft: '4px solid ' + color,
  }),
  summaryLabel: {
    fontSize: 11,
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  summaryValue: {
    fontSize: 20,
    fontWeight: 800,
    fontFamily: 'JetBrains Mono, monospace',
    color: 'var(--text-primary)',
  },
  currencyBadge: {
    fontSize: '0.65rem',
    fontWeight: 700,
    background: 'var(--bg-tertiary)',
    color: 'var(--text-secondary)',
    padding: '2px 8px',
    borderRadius: 'var(--r-full)',
    fontFamily: 'JetBrains Mono, monospace',
  },
};
